<script setup lang="ts">
/**
 * 客戶「基本資料」卡——唯讀顯示 ＋ **我方／代理商代填**（批97 Bug 7）。
 *
 * ## 為什麼是共用元件
 *
 * 主人裁示 6：我方後台（`views/CustomersView.vue`）與代理商後台（`views/reseller/
 * ResellerCustomersView.vue`）**同一張表單、同一套驗證**。後端也只有一份規則
 * （`services/customer_patch.py`，兩支 PUT 共用），兩個 realm 的 422 形狀
 * `{code, field, message}` 逐字相同——所以這支元件**不知道**自己在哪個 realm：
 * 差別全在兩個 prop（`loadOptions` 與 `save` 各打自己的端點）。
 *
 * 各寫一份的下場是「代理商那邊統編沒驗」或「我方那邊 422 少一個 field」這種
 * 不會有任何症狀的洞。
 *
 * ## ★ 未填資料的客戶也要有「編輯」鈕
 *
 * 那正是**主場景**：業務在電話裡問到資料，回來後台代填。批81 只做唯讀時，未填的客戶
 * 連一張表都沒有（只有一句「客戶還沒填」），所以這裡的唯讀 `dl` 一律渲染，
 * `profile_completed_at` 為 null 只是多一句提示。
 *
 * ## 代填不代同意
 *
 * 條款同意（`terms_*`）與邀請碼**不在表單上**，後端基底也沒有那些欄。代填完客戶下次
 * 登入 portal 仍會被要求勾條款，但表單已由代填值預填，他只要看一眼按送出。
 *
 * ## 電話收兩段
 *
 * DB 存 E.164（`+886912345678`），人是分兩格輸入的。進編輯態時用選項清單的國碼做
 * **最長前綴**比對拆回兩段（`+886` 與 `+88` 同時在清單裡時，短的先命中會切錯），
 * 送出時原樣送兩段、由後端 `normalize_phone` 組——前端不要自己組。
 */
import { computed, ref } from 'vue'

import UiButton from './UiButton.vue'
import { ApiError } from '@/api/client'
import type {
  CustomerPatchBody,
  CustomerProfile,
  CustomerProfileOptions,
  ProfileOption,
} from '@/api/ls'
import { vEnter } from '@/lib/enterKey'
import { useDialogsStore } from '@/stores/dialogs'

const props = defineProps<{
  /** 目前的基本資料（後端投影 `customer_profile_out`，兩個 realm 同形）。 */
  profile: CustomerProfile
  /** 內部備註（`customers.notes`）——客戶看不到，所以與 profile 分開傳。 */
  notes: string | null
  /** 有沒有編輯權（我方：admin+；代理商：自家客戶一律可編）。 */
  canEdit: boolean
  /** 懶載下拉選項（我方／代理商各自的端點）。只在第一次進編輯態時呼一次。 */
  loadOptions: () => Promise<CustomerProfileOptions>
  /** 送出部分更新（我方／代理商各自的 PUT）。回應含 `profile`，原樣往上 emit。 */
  save: (body: CustomerPatchBody) => Promise<unknown>
  /**
   * 批141-R：合作平台的組織＝平台名（其餘客戶不傳）。平台客戶**不開 portal**，所以
   * 「下次登入 portal 時會被要求補填」「條款同意」這類 portal 的說法一律不出現，
   * 改成一行「由合作平台 X 管理」。
   */
  managedByPlatform?: string | null
}>()
const emit = defineEmits<{ saved: [updated: unknown] }>()

const dialogs = useDialogsStore()

// --- 唯讀顯示 ---------------------------------------------------------------

const KIND_LABELS: Record<string, string> = { company: '公司／組織', personal: '個人' }
function kindLabel(kind: string | null): string {
  return kind ? (KIND_LABELS[kind] ?? kind) : '未填寫'
}
function fmtDt(iso: string | null): string {
  return iso ? iso.slice(0, 16).replace('T', ' ') : '—'
}

// --- 編輯態 -----------------------------------------------------------------

const editing = ref(false)
const saving = ref(false)
/** 選項清單；null＝還沒載（或載失敗——下拉退化成空清單，其他欄仍可填）。 */
const options = ref<CustomerProfileOptions | null>(null)
/** 已經試著載過了（成功或失敗都算）——避免每次進編輯態都重打一次。 */
const optionsTried = ref(false)

/** 選項載不回來時的國碼保底值。後端才是權威（`bad_option` 會擋），這裡只為了不空白。 */
const FALLBACK_COUNTRY_CODE = '+886'

const form = ref({
  kind: '',
  contact_name: '',
  title: '',
  country_code: FALLBACK_COUNTRY_CODE,
  phone_number: '',
  contact_email: '',
  line_id: '',
  tax_id: '',
  address: '',
  industry: '',
  heard_from: '',
  heard_from_other: '',
  company_size: '',
  /** 批114 裁示 ㉘：原本的欄位名是某個產品的數量詞，改成中性的「預計規模」（鍵名也跟著改）。 */
  expected_scale: '' as string | number,
  city: '',
  website: '',
  needs: '',
  notes: '',
  marketing_opt_in: false,
})

/** 422 的欄位級錯誤（後端只會回一個 field，所以一組 ref 就夠）。 */
const errField = ref('')
const errMessage = ref('')
function fieldError(name: string): string {
  return errField.value === name ? errMessage.value : ''
}
/** 電話兩段共用一行紅字（後端可能標在 `phone_number` 或 `country_code`）。 */
const phoneError = computed(() => fieldError('phone_number') || fieldError('country_code'))

const kinds = computed<ProfileOption[]>(() => options.value?.kinds ?? [])
const countryCodes = computed<ProfileOption[]>(() => options.value?.country_codes ?? [])
const industries = computed<ProfileOption[]>(() => options.value?.industries ?? [])
const heardFrom = computed<ProfileOption[]>(() => options.value?.heard_from ?? [])
const companySizes = computed<ProfileOption[]>(() => options.value?.company_sizes ?? [])
const cities = computed<ProfileOption[]>(() => options.value?.cities ?? [])

/**
 * E.164 → 兩段。**最長前綴優先**：`+1` 與 `+886` 同在清單裡時，用出現順序會把
 * `+886…` 切成 `+8` 之類的東西（現行清單沒有這種前綴關係，但清單是會被加東西的）。
 * 一個都對不上（舊資料格式怪）→ 國碼用預設、號碼放整串，人自己看一眼修。
 */
function splitPhone(
  phone: string | null,
  codes: ProfileOption[],
  fallback: string,
): { code: string; number: string } {
  const e164 = (phone ?? '').trim()
  if (!e164) return { code: fallback, number: '' }
  const byLength = [...codes].sort((a, b) => b.value.length - a.value.length)
  for (const c of byLength) {
    if (e164.startsWith(c.value)) return { code: c.value, number: e164.slice(c.value.length) }
  }
  return { code: fallback, number: e164 }
}

async function startEdit() {
  errField.value = ''
  errMessage.value = ''
  if (!optionsTried.value) {
    optionsTried.value = true
    try {
      options.value = await props.loadOptions()
    } catch {
      // 選項端點掛了不該讓人連編輯都進不去：下拉變空清單，輸入框照樣能填。
      await dialogs.notify({
        title: '選項清單讀取失敗',
        message: '下拉選單暫時是空的，其他欄位仍可填寫。重新整理頁面可再試一次。',
      })
    }
  }
  const p = props.profile
  const phone = splitPhone(
    p.phone,
    countryCodes.value,
    options.value?.default_country_code || FALLBACK_COUNTRY_CODE,
  )
  form.value = {
    kind: p.kind ?? '',
    contact_name: p.contact_name ?? '',
    title: p.title ?? '',
    country_code: phone.code,
    phone_number: phone.number,
    contact_email: p.contact_email ?? '',
    line_id: p.line_id ?? '',
    tax_id: p.tax_id ?? '',
    address: p.address ?? '',
    industry: p.industry ?? '',
    heard_from: p.heard_from ?? '',
    heard_from_other: p.heard_from_other ?? '',
    company_size: p.company_size ?? '',
    expected_scale: p.expected_scale ?? '',
    city: p.city ?? '',
    website: p.website ?? '',
    needs: p.needs ?? '',
    notes: props.notes ?? '',
    marketing_opt_in: p.marketing_opt_in,
  }
  editing.value = true
}

function cancelEdit() {
  editing.value = false
  errField.value = ''
  errMessage.value = ''
}

/** 空字串 → null（後端 `clean_str` 也會做，但別讓 `""` 出現在稽核 diff 裡）。 */
function nz(v: string): string | null {
  return v.trim() || null
}

function buildBody(): CustomerPatchBody {
  const f = form.value
  return {
    kind: nz(f.kind),
    contact_name: nz(f.contact_name),
    title: nz(f.title),
    // 兩段一律成對送：任一有帶後端就重算 E.164，號碼清空＝清掉電話。
    country_code: nz(f.country_code),
    phone_number: nz(f.phone_number),
    contact_email: nz(f.contact_email),
    line_id: nz(f.line_id),
    tax_id: nz(f.tax_id),
    address: nz(f.address),
    industry: nz(f.industry),
    heard_from: nz(f.heard_from),
    // 選了別的來源就把自填欄一起清掉（留著會是對不上的殘留值）。
    heard_from_other: f.heard_from === 'other' ? nz(f.heard_from_other) : null,
    company_size: nz(f.company_size),
    expected_scale: f.expected_scale === '' ? null : Number(f.expected_scale),
    city: nz(f.city),
    website: nz(f.website),
    // ★ 批114 裁示 ㉞：「主要客服通路」整題拿掉（那是某一個產品的欄位）。這裡**不送**
    //   這個鍵——PUT 是部分更新，不送＝不動，客戶既有的值留在資料庫裡不刪。
    needs: nz(f.needs),
    notes: nz(f.notes),
    marketing_opt_in: f.marketing_opt_in,
  }
}

async function submit() {
  if (saving.value) return
  saving.value = true
  errField.value = ''
  errMessage.value = ''
  // ★ 通知放最後、狀態先收乾淨：`dialogs.notify()` 要等人按掉才 resolve，
  //   在 try/finally 裡 await 會讓 `saving` 卡在 true（按鈕永遠轉圈）。
  let notice: { title: string; message: string } | null = null
  try {
    const updated = await props.save(buildBody())
    editing.value = false
    emit('saved', updated)
  } catch (e) {
    const detail =
      e instanceof ApiError && e.payload
        ? (e.payload as { detail?: unknown }).detail
        : undefined
    if (e instanceof ApiError && e.status === 422 && detail && typeof detail === 'object') {
      const d = detail as { field?: string; message?: string }
      errField.value = d.field ?? ''
      errMessage.value = d.message ?? '欄位內容不正確。'
      notice = { title: '有欄位需要修正', message: errMessage.value }
    } else {
      const text =
        typeof detail === 'string'
          ? detail
          : ((detail as { message?: string } | undefined)?.message ??
            '請稍後再試；若持續失敗請看瀏覽器主控台的錯誤。')
      notice = { title: '儲存基本資料失敗', message: text }
    }
  } finally {
    saving.value = false
  }
  if (notice) await dialogs.notify(notice)
}
</script>

<template>
  <div class="pf-card">
    <div class="pf-head">
      <h3 class="section-title">基本資料</h3>
      <span class="spacer" />
      <!-- ★ 未填資料的客戶也要有這顆鈕（代填正是主場景） -->
      <UiButton v-if="canEdit && !editing" size="sm" @click="startEdit">編輯</UiButton>
    </div>

    <template v-if="!editing">
      <p v-if="managedByPlatform" class="subtle pf-empty">
        由合作平台「{{ managedByPlatform }}」管理——這個組織不使用客戶 portal，基本資料不會由客戶自己補填；需要留存聯絡資料時可按「編輯」由我方填寫（僅供內部參考）。
      </p>
      <p v-else-if="!profile.profile_completed_at" class="subtle pf-empty">
        這位客戶還沒填寫基本資料——下次登入 portal 時會被要求補填，或按「編輯」由我方代填
        （代填不代同意條款）。
      </p>
      <dl class="pf">
        <div><dt>帳號類型</dt><dd>{{ kindLabel(profile.kind) }}</dd></div>
        <div><dt>聯絡人</dt><dd>{{ profile.contact_name ?? '—' }}</dd></div>
        <div><dt>職稱</dt><dd>{{ profile.title ?? '—' }}</dd></div>
        <div><dt>電話</dt><dd>{{ profile.phone ?? '—' }}</dd></div>
        <div><dt>LINE ID</dt><dd>{{ profile.line_id ?? '—' }}</dd></div>
        <div v-if="profile.kind === 'company'">
          <dt>統一編號</dt><dd>{{ profile.tax_id ?? '—' }}</dd>
        </div>
        <div><dt>聯絡 Email</dt><dd>{{ profile.contact_email ?? '—' }}</dd></div>
        <div><dt>地址</dt><dd>{{ profile.address ?? '—' }}</dd></div>
        <div><dt>產業別</dt><dd>{{ profile.industry_label ?? '—' }}</dd></div>
        <div>
          <dt>從哪聽到</dt>
          <dd>
            {{ profile.heard_from_label ?? '—' }}
            <span v-if="profile.heard_from_other" class="subtle">
              （{{ profile.heard_from_other }}）
            </span>
          </dd>
        </div>
        <div><dt>公司規模</dt><dd>{{ profile.company_size ?? '—' }}</dd></div>
        <div><dt>預計規模</dt><dd>{{ profile.expected_scale ?? '—' }}</dd></div>
        <!-- 批114 裁示 ㉜：客戶在〈組織資料〉選的主要產品（之後申請授權／代管的預設值）。
             產品被刪／停售時後端給不出中文名 → 退回印 key，總比印「—」看不出他選過什麼好。 -->
        <div>
          <dt>主要產品</dt>
          <dd>{{ profile.primary_product_name ?? profile.primary_product ?? '—' }}</dd>
        </div>
        <div><dt>網站／粉專</dt><dd>{{ profile.website ?? '—' }}</dd></div>
        <div><dt>所在縣市</dt><dd>{{ profile.city ?? '—' }}</dd></div>
        <div><dt>行銷通知</dt><dd>{{ profile.marketing_opt_in ? '願意接收' : '不接收' }}</dd></div>
        <!-- 條款同意／填寫時間都是客戶在 portal 上做的事——平台客戶不開 portal，不列。 -->
        <div v-if="!managedByPlatform">
          <dt>條款同意</dt>
          <dd>
            {{ fmtDt(profile.terms_accepted_at) }}
            <span v-if="profile.terms_version" class="subtle">
              （版本 {{ profile.terms_version }}）
            </span>
          </dd>
        </div>
        <div v-if="!managedByPlatform"><dt>填寫時間</dt><dd>{{ fmtDt(profile.profile_completed_at) }}</dd></div>
        <div v-if="profile.needs" class="wide">
          <dt>需求描述</dt><dd class="needs">{{ profile.needs }}</dd>
        </div>
        <div v-if="notes" class="wide">
          <dt>內部備註</dt><dd class="needs">{{ notes }}</dd>
        </div>
      </dl>
    </template>

    <template v-else>
      <p v-if="managedByPlatform" class="subtle pf-hint">
        由合作平台「{{ managedByPlatform }}」管理的組織不使用客戶 portal——這裡填的資料只供我方內部參考。
      </p>
      <p v-else class="subtle pf-hint">
        代填不代同意：條款同意與邀請碼不在這張表上，客戶下次登入 portal 仍會被要求勾選
        （表單會用這裡填的值預填）。
      </p>
      <div class="form">
        <label class="field">
          <span class="field-label">帳號類型</span>
          <select v-model="form.kind">
            <option value="">未填寫</option>
            <option v-for="o in kinds" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <small v-if="fieldError('kind')" class="err">{{ fieldError('kind') }}</small>
        </label>

        <label class="field">
          <span class="field-label">聯絡人</span>
          <input v-model="form.contact_name" placeholder="王小明" v-enter="submit" />
          <small v-if="fieldError('contact_name')" class="err">
            {{ fieldError('contact_name') }}
          </small>
        </label>

        <label class="field">
          <span class="field-label">職稱</span>
          <input v-model="form.title" placeholder="行銷經理" v-enter="submit" />
          <small v-if="fieldError('title')" class="err">{{ fieldError('title') }}</small>
        </label>

        <!-- ★ 電話用 `.field` 包一層 `.field-row`：全域 `.field > input/select` 是**子**
             選擇器，兩段控制項是孫節點才吃得到下面的 130px／flex:1，不會各自撐成全寬。 -->
        <div class="field">
          <span class="field-label">電話</span>
          <div class="field-row">
            <select v-model="form.country_code" class="cc">
              <option v-for="c in countryCodes" :key="c.value" :value="c.value">
                {{ c.label }}
              </option>
            </select>
            <input v-model="form.phone_number" class="pn" placeholder="0912345678" v-enter="submit" />
          </div>
          <small v-if="phoneError" class="err">{{ phoneError }}</small>
        </div>

        <label class="field">
          <span class="field-label">聯絡 Email</span>
          <input v-model="form.contact_email" placeholder="contact@acme.com" v-enter="submit" />
          <small v-if="fieldError('contact_email')" class="err">
            {{ fieldError('contact_email') }}
          </small>
        </label>

        <label class="field">
          <span class="field-label">LINE ID</span>
          <input v-model="form.line_id" placeholder="@yourshop" v-enter="submit" />
          <small v-if="fieldError('line_id')" class="err">{{ fieldError('line_id') }}</small>
        </label>

        <label class="field">
          <span class="field-label">統一編號</span>
          <input v-model="form.tax_id" placeholder="8 碼數字" maxlength="8" v-enter="submit" />
          <small v-if="fieldError('tax_id')" class="err">{{ fieldError('tax_id') }}</small>
        </label>

        <label class="field">
          <span class="field-label">地址</span>
          <input v-model="form.address" placeholder="台北市信義區…" v-enter="submit" />
          <small v-if="fieldError('address')" class="err">{{ fieldError('address') }}</small>
        </label>

        <label class="field">
          <span class="field-label">產業別</span>
          <select v-model="form.industry">
            <option value="">請選擇</option>
            <option v-for="o in industries" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <small v-if="fieldError('industry')" class="err">{{ fieldError('industry') }}</small>
        </label>

        <label class="field">
          <span class="field-label">從哪聽到</span>
          <select v-model="form.heard_from">
            <option value="">請選擇</option>
            <option v-for="o in heardFrom" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <small v-if="fieldError('heard_from')" class="err">{{ fieldError('heard_from') }}</small>
        </label>

        <label v-if="form.heard_from === 'other'" class="field">
          <span class="field-label">其他（自填）</span>
          <input v-model="form.heard_from_other" placeholder="例如：同業轉介" v-enter="submit" />
          <small v-if="fieldError('heard_from_other')" class="err">
            {{ fieldError('heard_from_other') }}
          </small>
        </label>

        <label class="field">
          <span class="field-label">公司規模</span>
          <select v-model="form.company_size">
            <option value="">請選擇</option>
            <option v-for="o in companySizes" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <small v-if="fieldError('company_size')" class="err">
            {{ fieldError('company_size') }}
          </small>
        </label>

        <label class="field">
          <span class="field-label">預計規模</span>
          <input v-model="form.expected_scale" type="number" min="1" placeholder="例如 20" />
          <small v-if="fieldError('expected_scale')" class="err">
            {{ fieldError('expected_scale') }}
          </small>
        </label>

        <label class="field">
          <span class="field-label">所在縣市</span>
          <select v-model="form.city">
            <option value="">請選擇</option>
            <option v-for="o in cities" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <small v-if="fieldError('city')" class="err">{{ fieldError('city') }}</small>
        </label>

        <label class="field">
          <span class="field-label">網站／粉專</span>
          <input v-model="form.website" placeholder="https://" v-enter="submit" />
          <small v-if="fieldError('website')" class="err">{{ fieldError('website') }}</small>
        </label>

        <!-- 批114 裁示 ㉞：「主要客服通路」整題拿掉（值域是某一個產品的通路代號）。
             客戶選的「主要產品」在唯讀表顯示，由客戶自己在 portal〈組織資料〉選，
             我方不代填——代填等於替客戶決定他買的是哪一套系統。 -->

        <label class="field wide">
          <span class="field-label">需求描述</span>
          <textarea v-model="form.needs" rows="3" placeholder="客戶想解決什麼問題" />
          <small v-if="fieldError('needs')" class="err">{{ fieldError('needs') }}</small>
        </label>

        <label class="field wide">
          <span class="field-label">內部備註（客戶看不到）</span>
          <textarea v-model="form.notes" rows="2" placeholder="我方內部紀錄，客戶端不會顯示" />
          <small v-if="fieldError('notes')" class="err">{{ fieldError('notes') }}</small>
        </label>

        <label class="field wide chk-field">
          <input v-model="form.marketing_opt_in" type="checkbox" />
          <span>願意接收產品消息與使用技巧</span>
        </label>
      </div>

      <div class="pf-foot">
        <UiButton variant="primary" size="sm" :loading="saving" @click="submit">儲存</UiButton>
        <UiButton size="sm" :disabled="saving" @click="cancelEdit">取消</UiButton>
      </div>
    </template>
  </div>
</template>

<style scoped>
.pf-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin: var(--sp-5) 0 var(--sp-3);
}
.pf-head .section-title {
  margin: 0;
}
.pf-head .spacer {
  flex: 1;
}
.pf-empty,
.pf-hint {
  margin: 0 0 var(--sp-3);
  line-height: var(--lh-base);
}

/* 唯讀：兩欄的 dt/dd 表（批97 從 CustomersView 搬過來，兩 realm 共用） */
.pf {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px var(--sp-4);
  margin: 0 0 var(--sp-3);
}
.pf > div {
  display: flex;
  gap: var(--sp-2);
  align-items: baseline;
  font-size: var(--fs-13);
  line-height: var(--lh-base);
}
.pf > div.wide {
  grid-column: 1 / -1;
  align-items: flex-start;
}
.pf dt {
  flex: 0 0 84px;
  color: var(--fg-3);
  font-size: var(--fs-12);
}
.pf dd {
  margin: 0;
  color: var(--fg);
  min-width: 0;
  word-break: break-word;
}
.needs {
  white-space: pre-wrap;
}

/* 編輯：兩欄表單（窄螢幕降單欄——右卡在 900px 以下會變成整寬的一欄） */
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}
@media (max-width: 700px) {
  .form {
    grid-template-columns: 1fr;
  }
}
.field.wide {
  grid-column: 1 / -1;
}
/* checkbox 那格不要 grid 的 5px 縱向間距，橫排對齊才像一句話 */
.chk-field {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg-2);
}
/* 電話兩段：國碼固定寬、號碼吃剩下的（都是 `.field-row` 的子節點＝全域規則的孫節點） */
.cc {
  width: 130px;
  flex: 0 0 auto;
}
.pn {
  flex: 1;
  min-width: 0;
}
.err {
  color: var(--danger);
  font-size: var(--fs-11);
  line-height: var(--lh-base);
}
.pf-foot {
  display: flex;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
}

/* 批114：膠囊式多選的樣式（`.picks`／`.pick`）隨「主要客服通路」那一題一起移除——
   這張表單已經沒有多選欄位，留著就是下一個人照抄的死碼。 */

/* 手機（批171）：唯讀的兩欄 dt/dd 表收成一欄（兩欄時值只剩三四個字寬，「公司／組織」、電話號碼都被折斷）；
   1fr 是 minmax(auto, 1fr)，一併釘成 minmax(0, 1fr)。 */
@media (--mobile) {
  .pf {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
